import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, X, Loader2, Pencil, Wand2, Mic, MessageSquare, Calendar,
  Ban, Timer, BellRing, ChevronDown, Play, Pause, Sparkles,
} from "lucide-react";
import { HelpCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  TONES, DAY_LABELS, EMPTY_SCRIPT, SCRIPT_VARIABLES,
  Row2, Field, LabelWithHint,
} from "./shared";

function Section({ id, title, icon: Icon, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-border" data-testid={`builder-section-${id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        data-testid={`builder-section-toggle-${id}`}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left hover:bg-muted/50">
        {Icon ? <Icon className="h-4 w-4 text-primary" /> : null}
        <span className="text-sm font-semibold text-foreground">{title}</span>
        <ChevronDown className={`ml-auto h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="border-t border-border px-3 py-3 space-y-3">{children}</div>}
    </div>
  );
}

function campaignToForm(c) {
  return {
    name: c.name || "",
    list_id: c.list_id || "",
    prompt: c.prompt || "",
    tone: c.tone || "conversational",
    qa_snippets: c.qa_snippets || [],
    calendly_link: c.calendly_link || "",
    retry_policy: { max_retries: 2, gap_hours: 24, voicemail_drop: false, sms_fallback: false, ...(c.retry_policy || {}) },
    tz_window: { start_hour: 9, end_hour: 18, ...(c.tz_window || {}) },
    dnc_enabled: c.dnc_enabled != null ? c.dnc_enabled : true,
    dnc_list_ids: Array.isArray(c.dnc_list_ids) ? c.dnc_list_ids : (c.dnc_list_id ? [c.dnc_list_id] : []),
    minutes_cap: c.minutes_cap != null ? c.minutes_cap : 500,
    daily_minutes_cap: c.daily_minutes_cap != null ? c.daily_minutes_cap : 60,
    auto_run: !!c.auto_run,
    voice_id: c.voice_id || "",
    active: c.active !== false,
    agent_name: c.agent_name || "",
    script: { ...EMPTY_SCRIPT(), ...(c.script || {}) },
    template_id: c.template_id || "",
    schedule: {
      timezone: "America/Edmonton", start_date: "", end_date: "",
      days_of_week: [0, 1, 2, 3, 4], start_time: "09:00", end_time: "18:00",
      country: "CA", block_holidays: true, blocked_dates: [], blocked_ranges: [], allowed_holidays: [],
      ...(c.schedule || {}),
    },
    alerts: { enabled: true, minutes_cap_warn_pct: 80, book_rate_floor_pct: 5, min_attempts_for_book_rate: 20, email_owner: false, ...(c.alerts || {}) },
  };
}

function defaultForm() {
  return {
    name: "", list_id: "", prompt: "", tone: "conversational",
    qa_snippets: [], calendly_link: "",
    retry_policy: { max_retries: 2, gap_hours: 24, voicemail_drop: false, sms_fallback: false },
    tz_window: { start_hour: 9, end_hour: 18 },
    dnc_enabled: true, dnc_list_ids: [], minutes_cap: 500, daily_minutes_cap: 60, auto_run: false,
    voice_id: "", active: true,
    agent_name: "", script: EMPTY_SCRIPT(), template_id: "",
    schedule: {
      timezone: "America/Edmonton",
      start_date: "", end_date: "",
      days_of_week: [0, 1, 2, 3, 4],
      start_time: "09:00", end_time: "18:00",
      country: "CA", block_holidays: true,
      blocked_dates: [], blocked_ranges: [], allowed_holidays: [],
    },
    alerts: { enabled: true, minutes_cap_warn_pct: 80, book_rate_floor_pct: 5, min_attempts_for_book_rate: 20, email_owner: false },
  };
}

function ScriptBuilder({ form, upd, updNested }) {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveDesc, setSaveDesc] = useState("");
  const [saveTags, setSaveTags] = useState("");
  const [saving, setSaving] = useState(false);
  const s = form.script || EMPTY_SCRIPT();

  const loadTemplates = () => {
    setLoadingTemplates(true);
    return api.get("/outbound/script-templates")
      .then((r) => setTemplates(r.data.templates || []))
      .catch(() => {}).finally(() => setLoadingTemplates(false));
  };
  useEffect(() => { loadTemplates(); }, []);

  const setScript = (patch) => upd("script", { ...s, ...patch });
  const applyTemplate = (tid) => {
    if (!tid) return;
    const t = templates.find((x) => x.template_id === tid);
    if (!t) return;
    if ((s.opening || s.value_prop || s.close || (s.value_bullets || []).length || (s.objections || []).length) &&
        !window.confirm(`Load "${t.name}" template? This will overwrite the current script sections.`)) return;
    upd("script", { ...EMPTY_SCRIPT(), ...t.script });
    upd("template_id", t.template_id);
    if (t.calendly_link && !form.calendly_link) {
      upd("calendly_link", t.calendly_link);
      toast.success(`Loaded template: ${t.name} · Calendly link copied to campaign`);
    } else {
      toast.success(`Loaded template: ${t.name}`);
    }
  };
  const addBullet = () => setScript({ value_bullets: [...(s.value_bullets || []), ""] });
  const setBullet = (i, v) => setScript({ value_bullets: s.value_bullets.map((b, j) => j === i ? v : b) });
  const rmBullet = (i) => setScript({ value_bullets: s.value_bullets.filter((_, j) => j !== i) });
  const addObjection = () => setScript({ objections: [...(s.objections || []), { trigger: "", response: "" }] });
  const setObjection = (i, k, v) => setScript({ objections: s.objections.map((o, j) => j === i ? { ...o, [k]: v } : o) });
  const rmObjection = (i) => setScript({ objections: s.objections.filter((_, j) => j !== i) });

  const scriptHasContent = !!(s.opening || s.value_prop || s.qualifying_question ||
    (s.value_bullets || []).some(Boolean) || s.call_to_action ||
    (s.objections || []).some((o) => o.trigger || o.response) || s.close);

  const doSave = async () => {
    if (!saveName.trim()) { toast.error("Template name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/outbound/script-templates", {
        name: saveName.trim(),
        description: saveDesc.trim(),
        script: s,
        tags: saveTags.split(",").map((t) => t.trim()).filter(Boolean),
        calendly_link: form.calendly_link || "",
      });
      toast.success(`Saved template "${data.name}"`);
      await loadTemplates();
      upd("template_id", data.template_id);
      setSaveOpen(false); setSaveName(""); setSaveDesc(""); setSaveTags("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save template"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="builder-script-section">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Label className="text-xs font-medium text-slate-600">Start from template (optional)</Label>
          <Select value={form.template_id || ""} onValueChange={applyTemplate}>
            <SelectTrigger data-testid="builder-script-template-select">
              <SelectValue placeholder={loadingTemplates ? "Loading templates…" : "Choose a template"} />
            </SelectTrigger>
            <SelectContent>
              {templates.map((t) => (
                <SelectItem key={t.template_id} value={t.template_id} data-testid={`builder-script-template-${t.template_id}`}>
                  {t.name}{t.is_seed ? " (built-in)" : ""}
                </SelectItem>
              ))}
              {templates.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No templates yet</div>}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" variant="outline" size="sm" className="h-9"
          onClick={() => { setSaveName(""); setSaveDesc(""); setSaveTags(""); setSaveOpen(true); }}
          disabled={!scriptHasContent}
          title={scriptHasContent ? "" : "Fill in at least one script section to save"}
          data-testid="builder-script-save-template-btn">
          <Plus className="h-3.5 w-3.5" /> Save as new template
        </Button>
      </div>

      <div className="rounded-md border border-slate-100 bg-slate-50/50 p-2 text-[10px] text-slate-500">
        <span className="font-medium text-slate-600">Variables:</span>{" "}
        {SCRIPT_VARIABLES.map((v, i) => (
          <span key={v.token} className="inline-block">
            <code className="rounded bg-white px-1 py-0.5 font-mono border border-slate-200" title={v.desc}>{v.token}</code>
            {i < SCRIPT_VARIABLES.length - 1 && <span className="mx-0.5">·</span>}
          </span>
        ))}
        <div className="mt-0.5 text-slate-400">Type these tokens anywhere in the sections below — they&apos;ll be replaced at call time with data from the recipient list, campaign, and Calendly.</div>
      </div>

      <Field label="Opening (hook)">
        <Textarea data-testid="builder-script-opening" rows={2} value={s.opening}
          onChange={(e) => setScript({ opening: e.target.value })}
          placeholder="Hi, this is {agent_name} calling on behalf of ..." />
      </Field>
      <Field label="Value prop">
        <Textarea data-testid="builder-script-valueprop" rows={3} value={s.value_prop}
          onChange={(e) => setScript({ value_prop: e.target.value })}
          placeholder="The reason for my call: we help ..." />
      </Field>
      <Field label="Qualifying question">
        <Textarea data-testid="builder-script-qualifying" rows={2} value={s.qualifying_question}
          onChange={(e) => setScript({ qualifying_question: e.target.value })}
          placeholder="Are you currently ...?" />
      </Field>

      <div className="rounded-md border border-slate-200 p-2">
        <div className="mb-1.5 flex items-center justify-between">
          <div className="text-xs font-medium text-slate-700">Value bullets (delivered if they engage)</div>
          <Button type="button" variant="outline" size="sm" onClick={addBullet} data-testid="builder-script-add-bullet">
            <Plus className="h-3 w-3" /> Add
          </Button>
        </div>
        {(s.value_bullets || []).length === 0 && <p className="text-[11px] text-slate-400">Optional. Short punchy lines the agent uses if the prospect engages.</p>}
        {(s.value_bullets || []).map((b, i) => (
          <div key={i} className="mb-1.5 flex gap-1" data-testid={`builder-script-bullet-row-${i}`}>
            <Input value={b} onChange={(e) => setBullet(i, e.target.value)}
              data-testid={`builder-script-bullet-${i}`} placeholder="One clear benefit statement" />
            <button type="button" onClick={() => rmBullet(i)} className="text-slate-300 hover:text-red-500"
              data-testid={`builder-script-bullet-remove-${i}`}><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>

      <Field label="Call to action">
        <Textarea data-testid="builder-script-cta" rows={2} value={s.call_to_action}
          onChange={(e) => setScript({ call_to_action: e.target.value })}
          placeholder="I'd love to set up a quick 15-minute call — does {day_time} work?" />
      </Field>

      <div className="rounded-md border border-slate-200 p-2">
        <div className="mb-1.5 flex items-center justify-between">
          <div className="text-xs font-medium text-slate-700">Objection handling</div>
          <Button type="button" variant="outline" size="sm" onClick={addObjection} data-testid="builder-script-add-objection">
            <Plus className="h-3 w-3" /> Add
          </Button>
        </div>
        {(s.objections || []).length === 0 && <p className="text-[11px] text-slate-400">Optional. Trigger phrases and how the agent should respond.</p>}
        {(s.objections || []).map((o, i) => {
          const isAi = o.source === "ai_suggestion";
          const removeAiHandler = async () => {
            if (!o.objection_id || !form.campaign_id) {
              rmObjection(i);
              return;
            }
            if (!window.confirm("Remove this AI-suggested rebuttal from the campaign?")) return;
            try {
              await api.post(`/outbound/campaigns/${form.campaign_id}/remove-objection`, {
                objection_id: o.objection_id,
                also_remove_from_template: false,
              });
              rmObjection(i);
              toast.success("Removed AI-suggested rebuttal.");
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Remove failed");
            }
          };
          return (
          <div key={o.objection_id || i} className="mb-2 grid grid-cols-1 gap-1 md:grid-cols-5" data-testid={`builder-script-objection-row-${i}`}>
            <div className="md:col-span-2 relative">
              <Input value={o.trigger || ""} onChange={(e) => setObjection(i, "trigger", e.target.value)}
                data-testid={`builder-script-objection-trigger-${i}`}
                placeholder='e.g. "Not interested" / "Already have a provider"' />
              {isAi && (
                <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary"
                      data-testid={`builder-script-objection-ai-badge-${i}`}
                      title={`Suggested by AI${o.added_at ? ` on ${new Date(o.added_at).toLocaleString()}` : ""}${o.based_on?.length ? ` · based on ${o.based_on.length} call${o.based_on.length === 1 ? "" : "s"}` : ""}`}>
                  <Sparkles className="h-2.5 w-2.5" /> AI
                </span>
              )}
            </div>
            <div className="md:col-span-3 flex gap-1">
              <Textarea rows={2} value={o.response || ""} onChange={(e) => setObjection(i, "response", e.target.value)}
                data-testid={`builder-script-objection-response-${i}`}
                placeholder="Totally understand — would it be alright if I sent a quick one-pager instead?" />
              <button type="button" onClick={isAi && o.objection_id && form.campaign_id ? removeAiHandler : () => rmObjection(i)}
                className={`self-start mt-1 ${isAi ? "text-primary/70 hover:text-red-500" : "text-slate-300 hover:text-red-500"}`}
                title={isAi ? "Remove AI-suggested rebuttal" : "Remove"}
                data-testid={`builder-script-objection-remove-${i}`}><X className="h-4 w-4" /></button>
            </div>
          </div>
          );
        })}
      </div>

      <Field label="Close">
        <Textarea data-testid="builder-script-close" rows={2} value={s.close}
          onChange={(e) => setScript({ close: e.target.value })}
          placeholder="Perfect, I'll get that set up. Thanks for your time, {recipient_name}." />
      </Field>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md" data-testid="builder-script-save-dialog">
          <DialogHeader>
            <DialogTitle>Save as new template</DialogTitle>
            <DialogDescription>
              Save the current script sections as a reusable template. Templates you create are visible to everyone in your organisation — the built-in templates cannot be overwritten.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Template name">
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g. MSP Cold Call — Alberta focus"
                data-testid="builder-script-save-name" autoFocus />
            </Field>
            <Field label="Description (optional)">
              <Textarea rows={2} value={saveDesc} onChange={(e) => setSaveDesc(e.target.value)}
                placeholder="Short summary of when to use this template — helps your team pick the right one."
                data-testid="builder-script-save-desc" />
            </Field>
            <Field label="Tags (optional, comma-separated)">
              <Input value={saveTags} onChange={(e) => setSaveTags(e.target.value)}
                placeholder="MSP, Alberta, Cold call"
                data-testid="builder-script-save-tags" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}
              data-testid="builder-script-save-cancel">Cancel</Button>
            <Button onClick={doSave} disabled={saving || !saveName.trim()}
              data-testid="builder-script-save-confirm"
              className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VoiceDot({ v }) {
  const tone = v.gender === "female" ? "bg-rose-400" : v.gender === "male" ? "bg-sky-500" : "bg-slate-400";
  return <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${tone}`} />;
}

function VoicePicker({ voices, value, onChange }) {
  const [playingId, setPlayingId] = useState(null);
  const [genderFilter, setGenderFilter] = useState("all");
  const [accentFilter, setAccentFilter] = useState("all");
  const audioRef = useRef(null);
  useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }, []);

  const play = (v) => {
    if (audioRef.current) { audioRef.current.pause(); }
    if (playingId === v.voice_id) { setPlayingId(null); return; }
    const audio = new Audio(v.preview_url);
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => { toast.error(`Preview unavailable for ${v.name}`); setPlayingId(null); };
    audio.play().catch(() => { toast.error(`Preview unavailable for ${v.name}`); setPlayingId(null); });
    audioRef.current = audio;
    setPlayingId(v.voice_id);
  };

  const genders = useMemo(() => Array.from(new Set(voices.map((v) => v.gender).filter(Boolean))).sort(), [voices]);
  const accents = useMemo(() => Array.from(new Set(voices.map((v) => v.accent).filter(Boolean))).sort(), [voices]);

  const filtered = useMemo(() => voices.filter((v) => (
    (genderFilter === "all" || v.gender === genderFilter) &&
    (accentFilter === "all" || v.accent === accentFilter)
  )), [voices, genderFilter, accentFilter]);

  useEffect(() => {
    if (!voices.length) return;
    const stillMatches = filtered.some((v) => v.voice_id === value);
    if (stillMatches) return;
    onChange(filtered.length > 0 ? filtered[0].voice_id : "");
  }, [genderFilter, accentFilter, voices.length]);

  const selected = voices.find((v) => v.voice_id === value);
  return (
    <div className="space-y-2" data-testid="builder-voice-section">
      <Label className="flex items-center gap-2 text-xs font-medium text-slate-600"><Mic className="h-3.5 w-3.5" /> Voice talent</Label>
      {voices.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">Loading voices…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger data-testid="builder-voice-filter-gender" className="h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All genders</SelectItem>
                {genders.map((g) => (
                  <SelectItem key={g} value={g} className="capitalize" data-testid={`builder-voice-filter-gender-${g}`}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={accentFilter} onValueChange={setAccentFilter}>
              <SelectTrigger data-testid="builder-voice-filter-accent" className="h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All accents</SelectItem>
                {accents.map((a) => (
                  <SelectItem key={a} value={a} data-testid={`builder-voice-filter-accent-${a}`}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger data-testid="builder-voice-select">
              <SelectValue placeholder={filtered.length ? "Pick a voice" : "No voices match those filters"}>
                {selected ? (
                  <span className="flex items-center gap-2">
                    <VoiceDot v={selected} /> {selected.name} · <span className="text-muted-foreground">{selected.gender} · {selected.accent}</span>
                  </span>
                ) : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {filtered.length === 0 ? (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">No voices match those filters — clear one above.</div>
              ) : filtered.map((v) => (
                <SelectItem key={v.voice_id} value={v.voice_id} data-testid={`builder-voice-option-${v.voice_id}`}>
                  <span className="flex items-center gap-2">
                    <VoiceDot v={v} />
                    <span className="font-medium">{v.name}</span>
                    <span className="text-xs text-muted-foreground">{v.gender} · {v.accent}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selected && filtered.some((v) => v.voice_id === selected.voice_id) && (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2" data-testid="builder-voice-preview">
              <button type="button" onClick={() => play(selected)} data-testid="builder-voice-preview-play"
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${playingId === selected.voice_id ? "bg-primary text-primary-foreground" : "border border-border bg-background text-foreground hover:border-primary/40 hover:text-primary"}`}>
                {playingId === selected.voice_id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground">{selected.name}</div>
                {(selected.tags || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid="builder-voice-tags">
                    {(selected.tags || []).map((t) => (
                      <span key={t} className="rounded-full border border-border bg-background/70 px-2 py-0.5 text-[10px] font-medium capitalize text-foreground/80">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                {selected.description && (
                  <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{selected.description}</div>
                )}
              </div>
              <div className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {playingId === selected.voice_id ? "playing" : "preview"}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DncMultiSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const selected = (options || []).filter((o) => (value || []).includes(o.dnc_list_id));
  const toggle = (id) => {
    const set = new Set(value || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    onChange(Array.from(set));
  };
  const removeChip = (id) => onChange((value || []).filter((x) => x !== id));
  return (
    <div className="space-y-2" data-testid="builder-dnc-multiselect">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="builder-dnc-multiselect-trigger"
        className="flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-left text-sm hover:border-primary/40">
        <span className="text-muted-foreground">
          {selected.length ? `${selected.length} list${selected.length === 1 ? "" : "s"} selected` : "None selected — click to attach"}
        </span>
        <Plus className={`h-4 w-4 transition-transform ${open ? "rotate-45" : ""}`} />
      </button>
      {open && (
        <div className="rounded-md border border-border bg-background p-1 max-h-60 overflow-y-auto" data-testid="builder-dnc-multiselect-menu">
          {(options || []).length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No DNC lists yet — create one in the DNC Lists tab.</div>}
          {(options || []).map((o) => {
            const checked = (value || []).includes(o.dnc_list_id);
            return (
              <label key={o.dnc_list_id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/60"
                data-testid={`builder-dnc-option-${o.dnc_list_id}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle(o.dnc_list_id)}
                  className="h-4 w-4 rounded border-border" />
                <span className="font-medium text-foreground">{o.name}</span>
                <span className="ml-auto text-xs text-muted-foreground">{o.entry_count || 0} entries</span>
              </label>
            );
          })}
        </div>
      )}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="builder-dnc-selected-chips">
          {selected.map((s) => (
            <span key={s.dnc_list_id}
              className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
              {s.name}
              <button type="button" onClick={() => removeChip(s.dnc_list_id)}
                data-testid={`builder-dnc-chip-remove-${s.dnc_list_id}`}
                className="ml-0.5 text-red-500 hover:text-red-700"><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ScheduleSection({ form, upd, updNested }) {
  const s = form.schedule || {};
  const [opts, setOpts] = useState({ timezones: [], countries: [] });
  const [holidays, setHolidays] = useState([]);
  const [newBlocked, setNewBlocked] = useState({ start: "", end: "", note: "" });

  useEffect(() => {
    api.get("/outbound/schedule-options").then((r) => setOpts(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!s.country) return;
    api.get("/outbound/holidays", { params: { country: s.country } })
      .then((r) => setHolidays(r.data.holidays || []))
      .catch(() => setHolidays([]));
  }, [s.country]);

  const toggleDay = (i) => {
    const cur = new Set(s.days_of_week || []);
    if (cur.has(i)) cur.delete(i); else cur.add(i);
    updNested("schedule", "days_of_week", Array.from(cur).sort((a, b) => a - b));
  };
  const addBlockedRange = () => {
    if (!newBlocked.start) { toast.error("Pick a start date"); return; }
    if (newBlocked.end && newBlocked.end < newBlocked.start) { toast.error("End date must be on or after start date"); return; }
    const range = {
      start: newBlocked.start,
      end: newBlocked.end || newBlocked.start,
      note: (newBlocked.note || "").trim(),
    };
    const list = [...(s.blocked_ranges || []), range]
      .sort((a, b) => (a.start || "").localeCompare(b.start || ""));
    updNested("schedule", "blocked_ranges", list);
    setNewBlocked({ start: "", end: "", note: "" });
  };
  const removeBlockedRange = (idx) =>
    updNested("schedule", "blocked_ranges", (s.blocked_ranges || []).filter((_, i) => i !== idx));
  const removeLegacyBlockedDate = (d) =>
    updNested("schedule", "blocked_dates", (s.blocked_dates || []).filter((x) => x !== d));
  const toggleAllowHoliday = (dateStr) => {
    const cur = new Set(s.allowed_holidays || []);
    if (cur.has(dateStr)) cur.delete(dateStr); else cur.add(dateStr);
    updNested("schedule", "allowed_holidays", Array.from(cur).sort());
  };
  const today = new Date().toISOString().slice(0, 10);
  const upcomingHolidays = holidays.filter((h) => h.date >= today).slice(0, 20);

  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="builder-schedule-section">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <Calendar className="h-4 w-4 text-primary" /> Schedule
      </div>
      <p className="mb-3 text-xs text-slate-400">
        When can this campaign actually place calls? Dates and days you exclude here will pause the queue automatically.
      </p>

      <Row2>
        <Field label="Timezone">
          <Select value={s.timezone || "America/Edmonton"} onValueChange={(v) => updNested("schedule", "timezone", v)}>
            <SelectTrigger data-testid="schedule-tz"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {(opts.timezones || []).map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Holiday country">
          <Select value={s.country || "CA"} onValueChange={(v) => updNested("schedule", "country", v)}>
            <SelectTrigger data-testid="schedule-country"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(opts.countries || []).map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </Row2>
      <Row2>
        <Field label="Start date">
          <Input type="date" data-testid="schedule-start-date" value={s.start_date || ""}
            onChange={(e) => updNested("schedule", "start_date", e.target.value)} />
        </Field>
        <Field label="End date">
          <Input type="date" data-testid="schedule-end-date" value={s.end_date || ""}
            onChange={(e) => updNested("schedule", "end_date", e.target.value)} />
        </Field>
      </Row2>
      <Row2>
        <Field label="Start time">
          <Input type="time" data-testid="schedule-start-time" value={s.start_time || "09:00"}
            onChange={(e) => updNested("schedule", "start_time", e.target.value)} />
        </Field>
        <Field label="End time">
          <Input type="time" data-testid="schedule-end-time" value={s.end_time || "18:00"}
            onChange={(e) => updNested("schedule", "end_time", e.target.value)} />
        </Field>
      </Row2>

      <div className="mt-2">
        <Label className="text-xs font-medium text-slate-600">Days of week</Label>
        <div className="mt-1 flex flex-wrap gap-1.5" data-testid="schedule-days">
          {DAY_LABELS.map((d, i) => {
            const on = (s.days_of_week || []).includes(i);
            return (
              <button key={d} type="button" onClick={() => toggleDay(i)}
                data-testid={`schedule-day-${i}`}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:border-primary/40"}`}>
                {d}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 text-sm text-slate-700">
        <Switch data-testid="schedule-block-holidays" checked={!!s.block_holidays}
          onCheckedChange={(v) => updNested("schedule", "block_holidays", v)} />
        Block public holidays in {opts.countries?.find((c) => c.code === s.country)?.name || s.country}
      </div>

      {s.block_holidays && upcomingHolidays.length > 0 && (
        <div className="mt-2 rounded-lg border border-border bg-muted/40 p-2" data-testid="schedule-holidays">
          <div className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">Upcoming holidays — click to allow calling on that day</div>
          <div className="flex flex-wrap gap-1.5">
            {upcomingHolidays.map((h) => {
              const allowed = (s.allowed_holidays || []).includes(h.date);
              return (
                <button key={h.date} type="button" onClick={() => toggleAllowHoliday(h.date)}
                  data-testid={`schedule-holiday-${h.date}`}
                  title={h.name}
                  className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${allowed
                    ? "border-emerald-300 bg-emerald-50 text-emerald-700 line-through decoration-slate-300"
                    : "border-red-200 bg-red-50 text-red-700"}`}>
                  {h.date} · {h.name}
                </button>
              );
            })}
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground">
            Red = blocked (holiday) · Green = you&apos;ve allowed calling that day
          </div>
        </div>
      )}

      <div className="mt-3">
        <Label className="text-xs font-medium text-slate-600">Additional blocked dates</Label>
        <p className="text-[10px] text-slate-400 mb-1.5">Pick a single date (leave End blank) or a consecutive range. Add an optional note explaining what the block is for — e.g. summer shutdown, company retreat, product launch freeze.</p>
        <div className="flex flex-wrap items-end gap-1.5">
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Start</Label>
            <Input type="date" value={newBlocked.start}
              onChange={(e) => setNewBlocked((v) => ({ ...v, start: e.target.value }))}
              data-testid="schedule-blocked-start" className="h-8 w-40" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">End <span className="text-slate-400 lowercase">(optional)</span></Label>
            <Input type="date" value={newBlocked.end} min={newBlocked.start || undefined}
              onChange={(e) => setNewBlocked((v) => ({ ...v, end: e.target.value }))}
              data-testid="schedule-blocked-end" className="h-8 w-40" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500">Note</Label>
            <Input value={newBlocked.note}
              onChange={(e) => setNewBlocked((v) => ({ ...v, note: e.target.value }))}
              placeholder="e.g. Summer shutdown"
              data-testid="schedule-blocked-note" className="h-8" />
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addBlockedRange}
            data-testid="schedule-blocked-add-btn" className="h-8">
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>

        {((s.blocked_ranges || []).length > 0 || (s.blocked_dates || []).length > 0) && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="schedule-blocked-list">
            {(s.blocked_ranges || []).map((br, idx) => {
              const isRange = br.end && br.end !== br.start;
              const label = isRange ? `${br.start} → ${br.end}` : br.start;
              return (
                <span key={`br-${idx}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700"
                  data-testid={`schedule-blocked-chip-${idx}`}>
                  <span className="font-mono">{label}</span>
                  {br.note && <span className="text-slate-500">· {br.note}</span>}
                  <button type="button" onClick={() => removeBlockedRange(idx)}
                    data-testid={`schedule-blocked-remove-${idx}`}
                    className="ml-0.5 text-slate-400 hover:text-red-500" aria-label="Remove"><X className="h-3 w-3" /></button>
                </span>
              );
            })}
            {(s.blocked_dates || []).map((d) => (
              <span key={`bd-${d}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700"
                data-testid={`schedule-blocked-legacy-chip-${d}`}>
                <span className="font-mono">{d}</span>
                <span className="text-slate-400">· (legacy)</span>
                <button type="button" onClick={() => removeLegacyBlockedDate(d)}
                  data-testid={`schedule-blocked-legacy-remove-${d}`}
                  className="ml-0.5 text-slate-400 hover:text-red-500" aria-label="Remove"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AlertsConfigPanel({ form, upd, updNested }) {
  const a = form.alerts || {};
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="builder-alerts-section">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><BellRing className="h-4 w-4 text-primary" /> Alerts</div>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <Switch data-testid="builder-alerts-enabled" checked={!!a.enabled} onCheckedChange={(v) => updNested("alerts", "enabled", v)} />
          Enabled
        </label>
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Fire an in-app notification (and optionally email the owner) when a run hits a minutes or book-rate threshold.
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label={<LabelWithHint text="Minutes-cap warning at (% of cap)" testId="builder-alert-minutes-warn-info"
          hint="Fires a warning when this run has used at least this percentage of its Minutes cap. Useful for catching runaway campaigns before they hit the hard cap and auto-stop." />}>
          <Input type="number" min="0" max="100" value={a.minutes_cap_warn_pct ?? 80}
            data-testid="builder-alert-minutes-warn"
            onChange={(e) => updNested("alerts", "minutes_cap_warn_pct", Number(e.target.value))} />
        </Field>
        <Field label={<LabelWithHint text="Book-rate floor (%)" testId="builder-alert-book-floor-info"
          hint="Fires an alert if your booking rate (booked ÷ attempts) falls below this threshold. Signals a script, targeting or voice-quality problem worth reviewing early." />}>
          <Input type="number" min="0" max="100" step="0.1" value={a.book_rate_floor_pct ?? 5}
            data-testid="builder-alert-book-floor"
            onChange={(e) => updNested("alerts", "book_rate_floor_pct", Number(e.target.value))} />
        </Field>
        <Field label={<LabelWithHint text="Min attempts before book-rate alert" testId="builder-alert-min-attempts-info"
          hint="Book-rate alerts are only evaluated after this many attempts have been dialed. Prevents noisy 0% alerts during the first handful of calls when the sample size is too small to be meaningful." />}>
          <Input type="number" min="1" value={a.min_attempts_for_book_rate ?? 20}
            data-testid="builder-alert-min-attempts"
            onChange={(e) => updNested("alerts", "min_attempts_for_book_rate", Number(e.target.value))} />
        </Field>
        <div className="flex items-end">
          <TooltipProvider delayDuration={200}>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch data-testid="builder-alert-email-owner" checked={!!a.email_owner}
                onCheckedChange={(v) => updNested("alerts", "email_owner", v)} />
              Email the campaign owner when an alert fires
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-slate-400 hover:text-slate-600"
                          data-testid="builder-alert-email-owner-info" aria-label="What does emailing the campaign owner do?">
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs" data-testid="builder-alert-email-owner-info-tooltip">
                  In addition to the in-app notification, an email is sent to the campaign owner via Resend. Off = notification stays in-app only (see the bell icon in the header).
                </TooltipContent>
              </Tooltip>
            </label>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
}

export function CampaignBuilderDialog({ open, onClose, lists, dncLists = [], campaign, onSaved }) {
  const isEdit = !!campaign;
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [voices, setVoices] = useState([]);
  useEffect(() => {
    if (!open) return;
    setForm(campaign ? campaignToForm(campaign) : defaultForm());
    api.get("/outbound/voices").then((r) => {
      setVoices(r.data.voices || []);
      if (!campaign && r.data.default_voice_id) {
        setForm((f) => f.voice_id ? f : ({ ...f, voice_id: r.data.default_voice_id }));
      }
    }).catch(() => {});
  }, [open, campaign]);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updNested = (k, sub, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [sub]: v } }));

  const addQA = () => upd("qa_snippets", [...(form.qa_snippets || []), { q: "", a: "" }]);
  const updQA = (i, key, v) => {
    const arr = [...form.qa_snippets]; arr[i] = { ...arr[i], [key]: v }; upd("qa_snippets", arr);
  };
  const rmQA = (i) => upd("qa_snippets", form.qa_snippets.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Campaign name is required"); return; }
    if (!isEdit && !form.list_id) { toast.error("Pick a recipient list"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const { list_id: _omit, ...rest } = form;
        await api.patch(`/outbound/campaigns/${campaign.campaign_id}`, rest);
        toast.success("Campaign updated");
        onSaved?.(campaign.campaign_id);
      } else {
        const { data } = await api.post("/outbound/campaigns", form);
        toast.success("Campaign created");
        onSaved?.(data.campaign_id);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-outbound-builder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-primary" />
            {isEdit ? `Edit campaign — ${campaign.name}` : "New campaign"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update prompts, tone, voice, alerts, and safety limits at any time — even while the campaign is in-flight."
              : "Configure how the AI agent will talk, which list to call, and safety limits."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Section id="basics" title="Basics" icon={Wand2} defaultOpen>
            <Row2>
              <Field label="Name">
                <Input data-testid="builder-name" value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Q1 renewals — enterprise" />
              </Field>
              <Field label="Recipient list">
                {isEdit ? (
                  <Input value={campaign.list_name || ""} disabled data-testid="builder-list-readonly" />
                ) : (
                  <Select value={form.list_id} onValueChange={(v) => upd("list_id", v)}>
                    <SelectTrigger data-testid="builder-list-select"><SelectValue placeholder="Choose a list" /></SelectTrigger>
                    <SelectContent>
                      {lists.length === 0 && <div className="px-2 py-1 text-xs text-slate-400">No lists yet — create one first</div>}
                      {lists.map((l) => (
                        <SelectItem key={l.list_id} value={l.list_id}>{l.name} · {l.recipient_count} recipients</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </Row2>
            <Row2>
              <Field label="Calendly link">
                <Input data-testid="builder-calendly" value={form.calendly_link} onChange={(e) => upd("calendly_link", e.target.value)}
                  placeholder="https://calendly.com/…" />
              </Field>
              <Field label={<LabelWithHint text="Agent name (used in scripts)" testId="builder-agent-name-info"
                hint="The name your AI agent introduces itself as on the call — e.g. 'Sarah'. Fills the {agent_name} token wherever it appears in your script. Independent of the ElevenLabs voice talent you choose; leave blank to default to the campaign owner's first name at runtime." />}>
                <Input data-testid="builder-agent-name" value={form.agent_name || ""}
                  onChange={(e) => upd("agent_name", e.target.value)} placeholder="Sarah" />
              </Field>
            </Row2>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch data-testid="builder-active-switch" checked={!!form.active}
                onCheckedChange={(v) => upd("active", v)} />
              Active — required to run. Inactive campaigns are skipped even if the date range is current.
            </label>
          </Section>

          <Section id="voice" title="Voice talent" icon={Mic}>
            <VoicePicker voices={voices} value={form.voice_id} onChange={(v) => upd("voice_id", v)} />
            <div className="mt-3">
              <Field label={<LabelWithHint text="Tone" testId="builder-tone-info"
                hint="Applied to campaign runs when ElevenLabs streaming is wired up. Conversational = natural moderate variance. Formal = steady, professional. Energetic = dynamic, more expressive. Empathetic = warm, slightly slower." />}>
                <Select value={form.tone} onValueChange={(v) => upd("tone", v)}>
                  <SelectTrigger data-testid="builder-tone"><SelectValue /></SelectTrigger>
                  <SelectContent>{TONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          <Section id="script" title="Script (structured, optional)" icon={MessageSquare}>
            <ScriptBuilder form={form} upd={upd} updNested={updNested} />
          </Section>

          <Section id="prompt" title="Prompt & Q&A snippets (fallback)" icon={MessageSquare}>
            <Field label="Prompt / opening script">
              <Textarea data-testid="builder-prompt" rows={4} value={form.prompt} onChange={(e) => upd("prompt", e.target.value)}
                placeholder="Hi, this is Ava from iplogin — I'm following up on our proposal to reduce your voice bill by 30%…" />
            </Field>
            <div className="mt-3 rounded-lg border border-slate-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-700">Q&amp;A snippets</div>
                <Button variant="outline" size="sm" data-testid="builder-add-qa" onClick={addQA}><Plus className="h-3.5 w-3.5" /> Add pair</Button>
              </div>
              {(form.qa_snippets || []).length === 0 && <p className="text-xs text-slate-400">Optional. Provide FAQ Q/A pairs so the agent stays on message.</p>}
              {(form.qa_snippets || []).map((qa, i) => (
                <div key={i} className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-2" data-testid={`builder-qa-row-${i}`}>
                  <Input placeholder="Question" value={qa.q} onChange={(e) => updQA(i, "q", e.target.value)} data-testid={`builder-qa-q-${i}`} />
                  <div className="flex gap-1">
                    <Input placeholder="Answer" value={qa.a} onChange={(e) => updQA(i, "a", e.target.value)} data-testid={`builder-qa-a-${i}`} />
                    <button onClick={() => rmQA(i)} data-testid={`builder-qa-remove-${i}`} className="text-slate-300 hover:text-red-500"><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section id="schedule" title="Schedule & call window" icon={Calendar}>
            <ScheduleSection form={form} upd={upd} updNested={updNested} />
          </Section>

          <Section id="dnc" title="Do-Not-Call & retry rules" icon={Ban}>
            <Row2>
              <Field label="Max retries">
                <Input data-testid="builder-max-retries" type="number" min="0" max="10" value={form.retry_policy.max_retries}
                  onChange={(e) => updNested("retry_policy", "max_retries", Number(e.target.value))} />
              </Field>
              <Field label="Gap between retries (hours)">
                <Input data-testid="builder-gap-hours" type="number" min="0" value={form.retry_policy.gap_hours}
                  onChange={(e) => updNested("retry_policy", "gap_hours", Number(e.target.value))} />
              </Field>
            </Row2>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <TooltipProvider delayDuration={200}>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Switch data-testid="builder-dnc-switch" checked={form.dnc_enabled} onCheckedChange={(v) => upd("dnc_enabled", v)} />
                  Respect Do-Not-Call list
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-slate-400 hover:text-slate-600"
                              data-testid="builder-dnc-info" aria-label="What is Do-Not-Call?">
                        <HelpCircle className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs" data-testid="builder-dnc-tooltip">
                      Numbers on any attached Do-Not-Call list are skipped automatically. The attempt is logged as <span className="font-mono">do_not_call</span> (no dial) so you keep a full audit trail without ever ringing the recipient.
                    </TooltipContent>
                  </Tooltip>
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Switch data-testid="builder-voicemail-switch" checked={form.retry_policy.voicemail_drop}
                    onCheckedChange={(v) => updNested("retry_policy", "voicemail_drop", v)} />
                  Drop voicemail
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-slate-400 hover:text-slate-600"
                              data-testid="builder-voicemail-info" aria-label="What is Drop voicemail?">
                        <HelpCircle className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs" data-testid="builder-voicemail-tooltip">
                      When the call rolls to voicemail, the agent leaves a pre-recorded message (using this campaign&apos;s voice + opening prompt) instead of hanging up. Off = the agent hangs up on voicemail beep and the attempt is logged as <span className="font-mono">voicemail</span> only.
                    </TooltipContent>
                  </Tooltip>
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Switch data-testid="builder-sms-switch" checked={form.retry_policy.sms_fallback}
                    onCheckedChange={(v) => updNested("retry_policy", "sms_fallback", v)} />
                  SMS fallback
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-slate-400 hover:text-slate-600"
                              data-testid="builder-sms-info" aria-label="What is SMS fallback?">
                        <HelpCircle className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs" data-testid="builder-sms-tooltip">
                      If a recipient doesn&apos;t answer (or the max retry count is hit without a connection), send a follow-up SMS containing your Calendly booking link and a one-line pitch. Requires an SMS-enabled sender number on your account.
                    </TooltipContent>
                  </Tooltip>
                </label>
              </TooltipProvider>
            </div>
            {form.dnc_enabled && (
              <div className="mt-3">
                <Field label="Do-Not-Call lists to apply">
                  <DncMultiSelect value={form.dnc_list_ids || []} onChange={(ids) => upd("dnc_list_ids", ids)} options={dncLists} />
                </Field>
              </div>
            )}
          </Section>

          <Section id="budget-alerts" title="Budget & alerts" icon={Timer}>
            <Row2>
              <Field label={<LabelWithHint text="Total minutes cap" testId="builder-minutes-cap-info"
                hint="Hard ceiling for the whole campaign. Once talk time hits this number, the dialer stops placing new calls until you top it up under Add minutes." />}>
                <Input data-testid="builder-minutes-cap" type="number" min="0" step="10" value={form.minutes_cap}
                  onChange={(e) => upd("minutes_cap", Number(e.target.value))} />
              </Field>
              <Field label={<LabelWithHint text="Daily minutes cap" testId="builder-daily-cap-info"
                hint="Rolling-24-hour ceiling used by the auto-dialer. Set lower than the total cap to spread calls across days rather than burning through them in one afternoon." />}>
                <Input data-testid="builder-daily-minutes-cap" type="number" min="0" step="5" value={form.daily_minutes_cap ?? 60}
                  onChange={(e) => upd("daily_minutes_cap", Number(e.target.value))} />
              </Field>
            </Row2>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3 mt-2 bg-amber-50/40">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800">Auto-dial this campaign</div>
                <div className="text-xs text-slate-500">
                  When ON, the outbound scheduler dials one recipient every 5 minutes — subject to the schedule, DNC, minutes caps, and the org kill-switch. Leave OFF to require manual <span className="font-medium">Dial next</span> clicks.
                </div>
              </div>
              <Switch data-testid="builder-auto-run" checked={!!form.auto_run}
                onCheckedChange={(v) => upd("auto_run", v)} />
            </div>
            <AlertsConfigPanel form={form} upd={upd} updNested={updNested} />
          </Section>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="builder-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="builder-save" className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />)}
            {isEdit ? "Save changes" : "Create campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
